/**
 * Toast/notification helper shared between debug overlays. Each consumer
 * gets its own private toast element + timeout state via the factory closure,
 * preserving the original "one singleton per file" semantics while removing
 * the 46-line near-identical bodies in src/debug/wthit-copy.ts and
 * src/level/gltf-hot-reloader.ts.
 */

interface ToastOptions {
  id: string
  topPx: number
  successColor: string
  successShadowRgba: string
}

const DEFAULT_OPTIONS: ToastOptions = {
  id: 'debug-toast',
  topPx: 78,
  successColor: 'rgba(0, 240, 255, 0.95)',
  successShadowRgba: 'rgba(0, 240, 255, 0.4)',
}

/**
 * Create a self-contained toast notifier. Returns a `show(message, isSuccess)`
 * function that lazily builds the DOM element on first use and reuses the
 * same singleton + fade timeout for every subsequent call.
 */
export function createDebugToast(options: Partial<ToastOptions> = {}) {
  const opts: ToastOptions = { ...DEFAULT_OPTIONS, ...options }
  let toastElement: HTMLDivElement | null = null
  let toastTimeout: ReturnType<typeof setTimeout> | null = null

  return function show(message: string, isSuccess = true): void {
    if (typeof document === 'undefined') return

    if (!toastElement) {
      toastElement = document.createElement('div')
      toastElement.id = opts.id
      toastElement.style.position = 'fixed'
      toastElement.style.top = `${opts.topPx}px`
      toastElement.style.left = '50%'
      toastElement.style.transform = 'translateX(-50%)'
      toastElement.style.padding = '8px 16px'
      toastElement.style.borderRadius = '4px'
      toastElement.style.fontFamily = "'Consolas', 'Courier New', monospace"
      toastElement.style.fontSize = '12px'
      toastElement.style.fontWeight = 'bold'
      toastElement.style.zIndex = '10000'
      toastElement.style.pointerEvents = 'none'
      toastElement.style.transition = 'opacity 0.2s ease, transform 0.2s ease'
      toastElement.style.boxShadow = `0 4px 20px rgba(0, 0, 0, 0.8), 0 0 12px ${opts.successShadowRgba}`
      document.body.appendChild(toastElement)
    }

    if (isSuccess) {
      toastElement.style.backgroundColor = opts.successColor
      toastElement.style.color = '#020617'
      toastElement.style.border = '1px solid #ffffff'
    } else {
      toastElement.style.backgroundColor = 'rgba(239, 68, 68, 0.95)'
      toastElement.style.color = '#ffffff'
      toastElement.style.border = '1px solid #fca5a5'
    }

    toastElement.textContent = message
    toastElement.style.display = 'block'
    toastElement.style.opacity = '1'

    if (toastTimeout) clearTimeout(toastTimeout)
    toastTimeout = setTimeout(() => {
      if (toastElement) {
        toastElement.style.opacity = '0'
        setTimeout(() => {
          if (toastElement) toastElement.style.display = 'none'
        }, 200)
      }
    }, 2200)
  }
}